import React, { useEffect, useRef, useState } from 'react';
import { fetchQueryById, searchQueriesForBooking } from '../../api/Query';
import { Query } from '../../types/Query';
import '../../css/modal.css';

interface Props {
  onClose: () => void;
  onSelect: (query: Query) => void;
}

const QueryPickerModal: React.FC<Props> = ({ onClose, onSelect }) => {
  const [results, setResults] = useState<Query[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const term = search.trim();
    const isNumeric = /^\d+$/.test(term);
    const shouldSearch = isNumeric ? term.length >= 1 : term.length >= 2;

    if (!shouldSearch) {
      setLoading(false);
      setError('');
      setHasSearched(false);
      setResults([]);
      return undefined;
    }

    const requestId = ++requestIdRef.current;
    const timer = window.setTimeout(() => {
      void (async () => {
        setLoading(true);
        setError('');
        setHasSearched(true);

        const mergedResults: Query[] = [];
        const seenIds = new Set<number>();
        const addResult = (query: Query | null | undefined) => {
          if (!query || !query.id || seenIds.has(query.id)) return;
          seenIds.add(query.id);
          mergedResults.push(query);
        };

        if (isNumeric) {
          try {
            const directQuery = await fetchQueryById(Number(term));
            if (requestIdRef.current !== requestId) return;
            addResult(directQuery);
          } catch {
            // Fall through to general search without surfacing the direct lookup failure.
          }
        }

        try {
          const searchResults = await searchQueriesForBooking({ q: term, limit: 25, page: 1 });
          if (requestIdRef.current !== requestId) return;
          searchResults.forEach(addResult);
          setResults(mergedResults);
          setError('');
        } catch (err: any) {
          if (requestIdRef.current !== requestId) return;
          if (mergedResults.length > 0) {
            setResults(mergedResults);
            setError('');
          } else {
            setResults([]);
            setError(err?.message || 'Failed to search queries');
          }
        } finally {
          if (requestIdRef.current === requestId) {
            setLoading(false);
          }
        }
      })();
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [search]);

  const resolvePhoneNumber = (query: Query) =>
    query.customer?.phoneNumber ||
    query.customer?.phone ||
    query.customer?.phone_number ||
    query.customerPhoneNumber ||
    '';

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: 560 }}>
        <button className="close-btn" onClick={onClose}>&times;</button>
        <h2>Select Query</h2>
        <input
          type="text"
          placeholder="Search by phone, customer name, query ID, or reference ID"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ maxHeight: 320, overflowY: 'auto', marginTop: 8 }}>
          {loading && <div>Loading…</div>}
          {!loading && !hasSearched && (
            <div>Search by phone, customer name, query ID, or reference ID</div>
          )}
          {!loading && hasSearched && error && (
            <div>Failed to search queries</div>
          )}
          {!loading && hasSearched && !error && results.length === 0 && (
            <div>No queries found</div>
          )}
          {!loading && !error && results.map(q => {
            const phone = resolvePhoneNumber(q);
            return (
              <div
                key={q.id}
                style={{ padding: '8px 6px', borderBottom: '1px solid #eee', cursor: 'pointer' }}
                onClick={() => onSelect(q)}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: 600 }}>{q.customer?.name || 'Customer'}</div>
                  <div style={{ fontSize: 12, color: '#555' }}>{`Query #${q.id}`}</div>
                  {q.referenceId && (
                    <span style={{ fontSize: 11, background: '#eef2ff', color: '#4338ca', padding: '2px 6px', borderRadius: 999 }}>
                      {q.referenceId}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
                  {[phone, q.type, q.location].filter(Boolean).join(' · ')}
                </div>
              </div>
            );
          })}
        </div>
        <div className="modal-actions">
          <button onClick={onClose} className="secondary">Close</button>
        </div>
      </div>
    </div>
  );
};

export default QueryPickerModal;
